import {useEffect, useState} from "react";
import {Stack, Switch, FormControlLabel, Select, MenuItem, SelectChangeEvent, Chip} from "@mui/material";
import {DateRange} from "@mui/x-date-pickers-pro";
import CalendarDialog from "../DateRangeCalendar/CalendarDialog";
import dayjs from "dayjs";
import {useTeamProgressContext} from "../../../../../../contexts/TeamProgressContext";
import {DateRangeFilterType} from "@/enums";
import {t} from "i18next";

const Filters = () => {
	const {setFilters, filters, allSectionsLoaded, sectionStates} = useTeamProgressContext();
	const {archivedUsers, dateRangeType, dateFrom, dateTo} = filters;
	const [openDialog, setOpenDialog] = useState<boolean>(false);
	const [enableFilters, setEnableFilters] = useState<boolean>(false);
	const isCustomDateRange = dateRangeType === DateRangeFilterType.customDateRange;

	useEffect(() => {
		const sectionsLoaded = allSectionsLoaded();
		setEnableFilters(sectionsLoaded);
	}, [sectionStates])

	const handleChangeDateRangeFilter = (event: SelectChangeEvent<DateRangeFilterType>) => {
		const selectedOption = event.target.value;
		if (selectedOption === DateRangeFilterType.customDateRange){
			setOpenDialog(true);
			return;
		}

		const dateUnit = selectedOption === DateRangeFilterType.lastWeek ? "week" :
			selectedOption === DateRangeFilterType.lastMonth ? "month" :
				"year";

		setFilters(previous => ({
			...previous,
			dateRangeType: selectedOption as DateRangeFilterType,
			dateFrom: dayjs().startOf(dateUnit).toDate(),
			dateTo: dayjs().startOf("day").toDate()
		}));
	}

	const handleChangeDateRange = (value: DateRange<Date>) => {
		setFilters(previous => ({
			...previous,
			dateRangeType: DateRangeFilterType.customDateRange,
			dateFrom: dayjs(value[0]).toDate(),
			dateTo: dayjs(value[1]).toDate()
		}));
	}

	const handleClearDateRange = () => {
		setFilters(previous => ({
			...previous,
			dateRangeType: DateRangeFilterType.lastWeek,
			dateFrom: dayjs().startOf("week").toDate(),
			dateTo: dayjs().startOf("day").toDate()
		}));
	}
	const handleCloseDialog = () => setOpenDialog(false);
	const handleChangeArchivedUserToggle = (_: React.SyntheticEvent, checked: boolean) => {
		setFilters(previous => ({...previous, archivedUsers: checked}));
	}

	return(
		<Stack
			direction={{xs: "column", md: "row"}}
		>
			<FormControlLabel
				control={<Switch checked={archivedUsers} />}
				label={t("team_progress_filter_archived_users", "Archived Users")}
				onChange={handleChangeArchivedUserToggle}
				disabled={!enableFilters}
			/>
			{
				!isCustomDateRange &&
				<Select
					onChange={handleChangeDateRangeFilter}
					value={dateRangeType as DateRangeFilterType}
					disabled={!enableFilters}
				>
					<MenuItem value={DateRangeFilterType.lastWeek}>{t("team_progress_filter_last_week", "This Week")}</MenuItem>
					<MenuItem value={DateRangeFilterType.lastMonth}>{t("team_progress_filter_last_month", "This Month")}</MenuItem>
					<MenuItem value={DateRangeFilterType.lastYear}>{t("team_progress_filter_last_year", "This Year")}</MenuItem>
					<MenuItem value={DateRangeFilterType.customDateRange}>{t("team_progress_filter_custom_date_range", "Custom Date Range")}</MenuItem>
				</Select>
			}
			{
				isCustomDateRange &&
				<Chip
					variant="outlined"
					color="primary"
					label={`${dateFrom ? dayjs(dateFrom).format("MM/DD/YYYY") : ""} - ${dateTo ? dayjs(dateTo).format("MM/DD/YYYY"): ""}`}
					onDelete={handleClearDateRange}
					sx={(theme) => ({
						"& .MuiChip-label": {
							color: theme.palette.common.white
						}
					})}
					disabled={!enableFilters}
				/>
			}
			{
				openDialog &&
				<CalendarDialog
					onAcceptDateRange={handleChangeDateRange}
					onClose={handleCloseDialog}
				/>
			}
		</Stack>
	)
}

export default Filters;